import React, { createContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnapshot } from "valtio";

import {
  Meta,
  dashboardStore,
  fetchNotificationStatus,
  metaStore,
  userStore,
  wellWishesStore,
} from "../hooks/store";
import { useAccountInfo } from "../hooks/useAccountInfo";
import { Dashboard } from "../types/dashboard";
import { Guest } from "../types/guest";
import { User } from "../types/user";
import { Routes } from "./baseRoutes";
import { ensureLightMode } from "./ensure.light.mode";

import { useWebSocketSetup } from "../hooks/useWebsocketSetup";
import { UserWeddingRole } from "../types/wedding";
import { WellWish } from "../types/wellwish";
import { browserNotificationManager } from "../utils/browserNotifications";
import { ClientStorage, ClientStorageItem } from "../utils/clientstorage";
import { fetchPackageBundlesAndBookings } from "../utils/package.bundle";
import { WSMessage } from "../utils/websocket";
import { getBasicWeddingInformation } from "./utils";

type ViewContext = "Editor" | "Preview";

export const CurrentAccountInfoContext = createContext<{
  meta: Meta;
  user: User;
  viewContext: ViewContext;
  setViewContext: (viewContext: ViewContext) => void;
  dashboard: Dashboard;
  wellWishes: {
    wellWishesCount: number;
    wellWishes: WellWish[];
  };
  setUser: (user: User) => void;
  setDashboard: (dashboard: Dashboard) => void;
  setWellWishes: (wellWishes: {
    wellWishesCount: number;
    wellWishes: WellWish[];
  }) => void;
  setMeta: (meta: Meta) => void;
  sendMessage: (wsmsg: WSMessage) => void;
}>({} as any);

interface AuthRouteProviderProps {
  children: React.ReactNode;
}

const AuthRouteProvider = ({ children }: AuthRouteProviderProps) => {
  const navigate = useNavigate();
  const userWeddingRole = ClientStorage.fetch(
    ClientStorageItem.UserWeddingRole
  ) as UserWeddingRole;
  const { user, dashboard, wellWishes, error } =
    useAccountInfo(userWeddingRole);
  const [viewContext, setViewContext] = useState<ViewContext>("Preview");
  const { showOnboardingModal } = useSnapshot(metaStore);
  const { sendMessage } = useWebSocketSetup();

  // TODO: useEffect hook to check for the cookie on mount
  useEffect(() => {
    ensureLightMode();
    // console.log("calling package bundles");
    fetchPackageBundlesAndBookings();
    const loggedInUserToken = ClientStorage.fetch(
      ClientStorageItem.AccessToken
    );
    if (!loggedInUserToken) {
      navigate(Routes.login);
    } else {
      const userWeddingRole = ClientStorage.fetch(
        ClientStorageItem.UserWeddingRole
      ) as UserWeddingRole;

      // fetch event planner specific data
      // if (userWeddingRole === UserWeddingRole.EventPlanner) {
      //   //
      // }
      if (userWeddingRole !== UserWeddingRole.EventPlanner) {
        getBasicWeddingInformation();
      }

      // Fetch notification status for all user types
      fetchNotificationStatus({
        onComplete: () => {
          // Notification status loaded successfully
        },
        onError: (err) => {
          console.error("Failed to fetch notification status:", err);
        },
      });

      // Request notification permission on app load
      browserNotificationManager.requestPermissionWithMessage();
    }
  }, [navigate, user?.weddingID]);

  if (error) {
    tokenExpiredSync();
    navigate(Routes.login);
  }

  const setUser = (currentUser: User) => {
    userStore.data = currentUser;
  };

  const setDashboard = (currentDashboard: Dashboard) => {
    dashboardStore.data = currentDashboard;
  };


  const setWellWishes = (currentWellWishes: {
    wellWishesCount: number;
    wellWishes: WellWish[];
  }) => {
    wellWishesStore.data = {
      ...wellWishesStore.data,
      ...currentWellWishes,
    };
  };

  const setMeta = (meta: Meta) => {
    metaStore.showOnboardingModal = meta.showOnboardingModal;
  };

  return (
    <CurrentAccountInfoContext.Provider
      value={{
        meta: {
          showOnboardingModal: showOnboardingModal,
        },
        user,
        viewContext,
        setViewContext,
        dashboard,
        setUser,
        setDashboard,
        setMeta,
        wellWishes,
        setWellWishes,
        sendMessage,
      }}
    >
      {children}
    </CurrentAccountInfoContext.Provider>
  );
};

export default AuthRouteProvider;

export const tokenExpiredSync = () => {
  ClientStorage.delete(ClientStorageItem.AccessToken);
  ClientStorage.store(ClientStorageItem.HasSeenWelcome, "false");
};
